import { DonutChart } from "@mantine/charts";
import {
  Anchor,
  Badge,
  Center,
  Divider,
  Group,
  HoverCard,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMatches,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { formatValueFromType } from "./utils";

import { TextSerif } from "@/components/elements";
import { IconDiamond } from "@/components/icons";
import { blue, green, orange, violet, yellow } from "@/config/chart_colors";
import { currencyMap, defaultCurrency } from "@/lib/currency";

import type { ChartColor, ChartColorTuple } from "@/config/chart_colors";
import type { Car, CarValue } from "@/types/car";
import type { Currency } from "@/types/currency";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";
import type { CarChartCell, CarChartValueType } from "./types";

export type CarsDonutChartProps = {
  color: ChartColor;
  getPath?: (carId: string) => string;
  groupBy?: (car: Car, value: number) => string;
  hideBadge?: boolean;
  title?: string;
  valueKey?: CarValue;
  valueType: CarChartValueType;
  values: Car[];
} & MantineStyleProps;

const colors: Record<ChartColor, ChartColorTuple> = {
  blue,
  green,
  orange,
  violet,
  yellow,
};

export const CarsDonutChart: FC<CarsDonutChartProps> = ({
  color,
  getPath,
  groupBy,
  hideBadge = false,
  title,
  valueKey = "current_value",
  valueType,
  values,
  ...props
}) => {
  const { t } = useTranslation();
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });
  const isMobile = useMatches({ base: true, md: false });
  const fontSize = { base: "sm", sm: "md" };
  const size = isMobile ? "sm" : "lg";

  const formatValue = (val: number) => {
    if (valueType === "percent") {
      return `${(val * 100).toFixed(1)}%`;
    }
    return formatValueFromType(valueType, currency)(val);
  };

  const data: CarChartCell[] = useMemo(() => {
    let colorShade = 0;

    const groups: Record<string, CarChartCell> = {};

    values.forEach((car) => {
      const rawValue = car[valueKey] || 0;
      const rate = Number(currencyMap[currency.value] ?? 1);
      const convertedValue = rawValue * rate;
      const key = groupBy ? groupBy(car, rawValue) : car.model || `${car.make} ${car.model}`;

      if (groups[key]) {
        groups[key].cars.push(car);
        groups[key].count += 1;
        groups[key].valueRaw += rawValue;
        groups[key].value += convertedValue;
      } else {
        groups[key] = {
          cars: [car],
          color: colors[color][colorShade],
          count: 1,
          name: key,
          valueRaw: rawValue,
          value: convertedValue,
        };
        colorShade = (colorShade + 1) % colors[color].length;
      }
    });

    const grouped = Object.values(groups).map((g) => {
      let finalValue: number;

      if (valueType === "percent") {
        const totalCount = values.length;
        finalValue = totalCount ? g.count / totalCount : 0;
      } else {
        finalValue = g.value;
      }

      return { ...g, value: finalValue };
    });

    grouped.sort((a, b) => b.value - a.value);

    return grouped;
  }, [valueType, values, currency, valueKey, groupBy, color]);

  return (
    <Stack {...props}>
      {title && <Title order={4}>{title}</Title>}
      <Center p="md">
        <DonutChart
          data={data}
          valueFormatter={formatValue}
          size={160}
          thickness={32}
          tooltipDataSource="segment"
        />
      </Center>
      <Group w="100%" justify="space-between">
        <Stack gap="sm" flex={1}>
          {data.map((item, index) => (
            <Group key={index} justify="space-between" wrap="nowrap">
              <Group gap="xs" align="center" wrap="nowrap">
                <ThemeIcon c={item.color} variant="transparent" size="xs">
                  <IconDiamond size={20} />
                </ThemeIcon>
                <TextSerif title={item.name} fz={fontSize} truncate>
                  {item.name}
                </TextSerif>
              </Group>
              <Group gap="md" wrap="nowrap">
                {!hideBadge && (
                  <HoverCard withArrow>
                    <HoverCard.Target>
                      <Badge size="md" variant="light-gray">
                        {t("garage.assets", { count: item.count })}
                      </Badge>
                    </HoverCard.Target>
                    <HoverCard.Dropdown
                      p={0}
                      style={{
                        maxHeight: 225,
                        maxWidth: 225,
                        minWidth: 225,
                        overflowY: "auto",
                      }}
                    >
                      <Stack gap="xs" p="sm">
                        {item.cars.map((car) => {
                          const rate = Number(currencyMap[currency.value] ?? 1);
                          const carValue = (car[valueKey] || 0) * rate;

                          let relativePercentInGroup = 0;

                          if (valueType === "percent") {
                            relativePercentInGroup = item.count ? 1 / item.count : 0;
                          } else {
                            const groupTotal = item.cars.reduce(
                              (sum, c) => sum + (c[valueKey] || 0) * rate,
                              0
                            );
                            relativePercentInGroup = groupTotal ? carValue / groupTotal : 0;
                          }

                          const finalValue =
                            valueType === "percent" ? relativePercentInGroup : carValue;

                          return (
                            <Group
                              key={car.identifier}
                              justify="space-between"
                              w="100%"
                              wrap="nowrap"
                              style={{ overflow: "hidden" }}
                            >
                              {getPath ? (
                                <Anchor
                                  component={Link}
                                  to={getPath(car.identifier)}
                                  size="sm"
                                  title={`${car.make} ${car.model}`}
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: 150,
                                    display: "inline-block",
                                  }}
                                >
                                  {car.make} {car.model}
                                </Anchor>
                              ) : (
                                <Text
                                  size="sm"
                                  title={`${car.make} ${car.model}`}
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: 150,
                                    display: "inline-block",
                                  }}
                                >
                                  {car.make} {car.model}
                                </Text>
                              )}
                              <TextSerif size="sm" title={formatValue(finalValue)}>
                                {formatValue(finalValue)}
                              </TextSerif>
                            </Group>
                          );
                        })}
                      </Stack>
                    </HoverCard.Dropdown>
                  </HoverCard>
                )}
                <Text size={size}>{formatValue(item.value)}</Text>
              </Group>
            </Group>
          ))}
        </Stack>
        <Divider orientation="vertical" />
      </Group>
    </Stack>
  );
};
